import {AnchoredOverlay, Button} from '@primer/react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCallback, useMemo, useState} from 'react'

import {TriageAgentConfigModal} from './TriageAgentConfigModal'
import {TriageAgentStatusIcon} from './TriageAgentStatusIcon'
import styles from './TriageAgentButton.module.css'
import {TriageAgentOverlayContent} from './TriageAgentOverlayContent'
import {useTriageAgentStatus} from './use-triage-agent-status'
import {TRIAGE_BUTTON_TEXT, type TriageState} from './utils/constants'

type TriageAgentButtonProps = {
  repoOwner: string
  repoName: string
}

const INDICATOR_CONFIG: Record<TriageState, {testId: string; ariaLabel: string; color: string}> = {
  inactive: {
    testId: 'triage-agent-status-inactive',
    ariaLabel: 'Triage agent is inactive',
    color: 'var(--borderColor-default)',
  },
  active: {
    testId: 'triage-agent-status-active',
    ariaLabel: 'Triage agent is active',
    color: 'var(--fgColor-success)',
  },
  warning: {
    testId: 'triage-agent-status-warning',
    ariaLabel: 'Triage agent has invalid configuration',
    color: 'var(--fgColor-muted)',
  },
}

const ERROR_CONFIG = {
  testId: 'triage-agent-status-error',
  ariaLabel: 'Error loading triage agent status',
  color: 'var(--fgColor-attention)',
}

function TriageAgentButtonLoading() {
  return (
    <Button disabled data-testid="triage-agent-button-loading">
      Triage agent
    </Button>
  )
}

function TriageAgentButtonLoaded({
  repoOwner,
  repoName,
  triageState,
  triageEnabled,
  isError,
  viewerCanConfigureTriageAgent,
  configEditorEnabled,
}: {
  repoOwner: string
  repoName: string
  triageState: TriageState
  triageEnabled: boolean
  isError: boolean
  viewerCanConfigureTriageAgent: boolean
  configEditorEnabled: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const config = isError ? ERROR_CONFIG : INDICATOR_CONFIG[triageState]

  const handleOpenConfigModal = useCallback(() => {
    setIsOpen(false)
    setIsConfigModalOpen(true)
  }, [])

  const handleConfigModalClose = useCallback(() => {
    setIsConfigModalOpen(false)
  }, [])

  const handleConfigSaved = useCallback(() => {
    setIsConfigModalOpen(false)
  }, [])

  const leadingVisual = useMemo(
    () => (
      <span
        data-testid={config.testId}
        className={`${styles.statusIndicator} ${triageState === 'active' ? styles.pulse : ''}`}
        style={{color: config.color}}
      >
        <TriageAgentStatusIcon
          state={triageState}
          ariaLabel={config.ariaLabel}
          isError={isError}
          triageEnabled={triageEnabled}
        />
      </span>
    ),
    [config, triageState, isError, triageEnabled],
  )

  return (
    <>
      <AnchoredOverlay
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        renderAnchor={props => (
          <Button
            {...props}
            leadingVisual={leadingVisual}
            className={triageState === 'warning' && !triageEnabled ? styles.mutedText : undefined}
          >
            {TRIAGE_BUTTON_TEXT[triageState]}
          </Button>
        )}
      >
        <TriageAgentOverlayContent
          repoOwner={repoOwner}
          repoName={repoName}
          onClose={() => setIsOpen(false)}
          onOpenConfigModal={handleOpenConfigModal}
          viewerCanConfigureTriageAgent={viewerCanConfigureTriageAgent}
          configEditorEnabled={configEditorEnabled}
        />
      </AnchoredOverlay>
      {configEditorEnabled && (
        <TriageAgentConfigModal
          isOpen={isConfigModalOpen}
          onClose={handleConfigModalClose}
          onSaved={handleConfigSaved}
          repoOwner={repoOwner}
          repoName={repoName}
        />
      )}
    </>
  )
}

export function TriageAgentButton({repoOwner, repoName}: TriageAgentButtonProps) {
  const {data, isLoading, isError} = useTriageAgentStatus(repoOwner, repoName)
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const configEditorEnabled = useFeatureFlag('issues_triage_agent_config_editor')

  if (isLoading) {
    return <TriageAgentButtonLoading />
  }

  const triageEnabled = !!data?.enabled
  const hasInvalidConfig = data?.config_valid === false
  const viewerCanConfigureTriageAgent = data?.viewer_can_configure ?? false

  let triageState: TriageState = 'inactive'
  if (hasInvalidConfig) {
    triageState = 'warning'
  } else if (triageEnabled) {
    triageState = 'active'
  }

  return (
    <TriageAgentButtonLoaded
      repoOwner={repoOwner}
      repoName={repoName}
      triageState={triageState}
      triageEnabled={triageEnabled}
      isError={isError}
      viewerCanConfigureTriageAgent={viewerCanConfigureTriageAgent}
      configEditorEnabled={configEditorEnabled}
    />
  )
}
